import { useCallback, useEffect } from 'react';
import type { ChangeEvent } from 'react';

import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import { useMediaQuery, useTheme } from '@mui/material';

import { useDialogAutoFocus } from '../../hooks/useDialogAutoFocus.hook';

import { emptyMemosState } from './utils/empty-memos-state.util';
import type { MemosState } from './types/memos-state.type';

export { emptyMemosState };
export type { MemosState };

interface Props {
  readonly submitLabel: string;
  readonly isLoading: boolean;
  readonly setMemos: (
    newValue: ((memos: MemosState) => MemosState) | MemosState,
  ) => void;
  readonly memos: MemosState;
  readonly clearMemos: () => void;
  readonly onConfirm: (() => unknown) | (() => Promise<unknown>);
  readonly onCancel?: (() => unknown) | (() => Promise<unknown>);
}

export const MemoBeforeSubmitForm = ({
  submitLabel,
  isLoading,
  memos,
  setMemos,
  clearMemos,
  onConfirm,
  onCancel,
}: Readonly<Props>) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const dialogAutoFocusSlotProps = useDialogAutoFocus();

  const handleSubmitMemo = useCallback(() => {
    setMemos((oldMemo) => ({
      ...oldMemo,
      hasBeenMemoed: true,
      isOpenMemoDialog: false,
    }));
  }, [setMemos]);

  const setInternalMemo = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setMemos((oldMemos) => ({ ...oldMemos, internalMemo: e.target.value }));
    },
    [setMemos],
  );

  const setMemo = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setMemos((oldMemos) => ({ ...oldMemos, memo: e.target.value }));
    },
    [setMemos],
  );

  useEffect(() => {
    const confirm = async () => {
      if (memos.hasBeenMemoed) {
        setMemos((oldMemo) => ({
          ...oldMemo,
          hasBeenMemoed: false,
        }));
        await onConfirm();
      }
    };

    void confirm();
  }, [onConfirm, memos.hasBeenMemoed, setMemos]);

  const handleCancel = useCallback(async () => {
    clearMemos();
    if (onCancel) {
      await onCancel();
    }
  }, [clearMemos, onCancel]);

  return (
    <Dialog
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      onClose={clearMemos}
      open={memos.isOpenMemoDialog}
      slotProps={dialogAutoFocusSlotProps}
    >
      <DialogTitle>Annotations</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 4, pt: 1, pb: 1 }}>
          Vous avez la possibilité d&apos;annoter cette version.
        </DialogContentText>
        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 12,
            }}
            sx={{ flexGrow: 1, pt: 1, pb: 1 }}
          >
            <TextField
              fullWidth
              id="internal-memo"
              label="Mémo interne"
              multiline
              name="internalMemo"
              onChange={setInternalMemo}
              rows={4}
              type="text"
              value={memos.internalMemo}
              variant="outlined"
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
            }}
            sx={{ flexGrow: 1, pt: 1, pb: 1 }}
          >
            <TextField
              fullWidth
              id="memo"
              label="Mémo"
              multiline
              name="memo"
              onChange={setMemo}
              rows={4}
              type="text"
              value={memos.memo}
              variant="outlined"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button
          data-dialog-autofocus
          loading={isLoading}
          onClick={handleSubmitMemo}
          size="large"
          type="submit"
          variant="contained"
        >
          {submitLabel}
        </Button>
        {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
        <Button onClick={handleCancel} size="large" variant="outlined">
          Annuler
        </Button>
      </DialogActions>
    </Dialog>
  );
};
